import { Button, Collapsible, Input } from '@cloudflare/kumo';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { cleanId } from '../../shared/clean-id.ts';
import { api, queryClient, unwrap } from '../api.ts';
import { ErrorNotice, Loading } from '../common.tsx';
import { CollapsibleTrigger } from './collapsible-trigger.tsx';
import { TransferInitiation } from './transfer-initiation.tsx';

export function SharingControls({ target }: { target: { siteId: string } | { accountId: string } }) {
  const siteId = 'siteId' in target ? target.siteId : undefined;
  const accountId = 'accountId' in target ? target.accountId : undefined;
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState('');
  const request = useRef<{ email: string; id: string } | null>(null);
  const sharing = useQuery({
    queryKey: ['sharing', target],
    queryFn: async () =>
      siteId
        ? unwrap(await api.GET('/api/sites/{siteId}/sharing', { params: { path: { siteId } } }))
        : unwrap(
            await api.GET('/api/accounts/{accountUserId}/billing-sharing', {
              params: { path: { accountUserId: accountId! } },
            }),
          ),
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['sharing'] });
  const invite = useMutation({
    mutationFn: async () => {
      const recipient = email.trim().toLowerCase();
      if (request.current?.email !== recipient) request.current = { email: recipient, id: cleanId() };
      const body = { invitationId: request.current.id, email: recipient };
      return siteId
        ? unwrap(await api.POST('/api/sites/{siteId}/invitations', { params: { path: { siteId } }, body }))
        : unwrap(
            await api.POST('/api/accounts/{accountUserId}/billing-invitations', {
              params: { path: { accountUserId: accountId! } },
              body,
            }),
          );
    },
    onSuccess: async (result) => {
      setSent(result.email);
      setEmail('');
      request.current = null;
      await invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: async (userId: string) =>
      siteId
        ? unwrap(
            await api.POST('/api/sites/{siteId}/collaborators/{userId}/removal', {
              params: { path: { siteId, userId } },
              body: {},
            }),
          )
        : unwrap(
            await api.POST('/api/accounts/{accountUserId}/billing-users/{userId}/removal', {
              params: { path: { accountUserId: accountId!, userId } },
              body: {},
            }),
          ),
    onSuccess: invalidate,
  });
  const revoke = useMutation({
    mutationFn: async (invitationId: string) =>
      siteId
        ? unwrap(
            await api.POST('/api/sites/{siteId}/invitations/{invitationId}/revocation', {
              params: { path: { siteId, invitationId } },
              body: {},
            }),
          )
        : unwrap(
            await api.POST('/api/accounts/{accountUserId}/billing-invitations/{invitationId}/revocation', {
              params: { path: { accountUserId: sharing.data!.accountId, invitationId } },
              body: {},
            }),
          ),
    onSuccess: invalidate,
  });
  const title = siteId ? 'People with site access' : 'Payment-method access';
  const info = sharing.data;
  const body = sharing.isPending ? (
    <Loading />
  ) : sharing.error ? (
    <ErrorNotice error={sharing.error} />
  ) : !info ? (
    <Loading />
  ) : (
    <>
      <p className="muted leading-[1.65] text-kumo-subtle text-[14px]">
        {siteId
          ? 'Collaborators can edit and publish. The owner manages billing, sharing, and ownership.'
          : 'These people can view invoices and update the payment method for the whole subscription. They cannot edit sites, read form entries, or change which sites are billed.'}
      </p>
      <ErrorNotice error={invite.error ?? remove.error ?? revoke.error} />
      {info.canManage ? (
        <form
          className="mx-0 my-6 flex flex-col items-stretch gap-3 [&_>_:first-child]:flex-1 min-[601px]:flex-row min-[601px]:items-end"
          onSubmit={(event) => {
            event.preventDefault();
            setSent('');
            invite.mutate();
          }}
        >
          <Input
            label="Invite by email"
            type="email"
            value={email}
            required
            onChange={(event) => setEmail(event.target.value)}
          />
          <Button type="submit" variant="primary" disabled={invite.isPending}>
            {invite.isPending ? 'Sending…' : 'Send invitation'}
          </Button>
        </form>
      ) : (
        <p className="leading-[1.65]">Only the account owner can change access.</p>
      )}
      {sent ? (
        <p className="leading-[1.65]" role="status">
          Invitation sent to {sent}. They’ll sign in with their own account.
        </p>
      ) : null}
      <ul className="p-0 list-none [&_li]:px-0 [&_li]:py-4 [&_li]:border-t [&_li]:border-t-kumo-line [&_li]:flex [&_li]:items-start [&_li]:justify-between [&_li]:gap-4 [&_li_>_div:first-child]:grid [&_li_>_div:first-child]:gap-1 [&_li_>_div:first-child]:wrap-anywhere [&_li_>_div:first-child]:min-w-0 [&_li_span]:text-kumo-subtle [&_li_span]:text-[0.875rem] min-[601px]:[&_li]:items-center">
        {info.people.map((person) => (
          <li key={person.userId}>
            <div>
              <strong>{person.name}</strong>
              <span>{person.email}</span>
            </div>
            <div>
              {person.owner ? (
                <span>Account owner</span>
              ) : info.canManage ? (
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Remove access for ${person.email}`}
                  onClick={() => remove.mutate(person.userId)}
                  disabled={remove.isPending}
                >
                  Remove access
                </Button>
              ) : (
                <span>{siteId ? 'Collaborator' : 'Payment-method access'}</span>
              )}
            </div>
          </li>
        ))}
        {info.invitations.map((invitation) => (
          <li key={invitation.id}>
            <div>
              <strong>{invitation.email}</strong>
              <span>Invited · expires {new Date(invitation.expiresAt).toLocaleDateString()}</span>
            </div>
            <Button
              size="sm"
              variant="ghost"
              aria-label={`Withdraw invitation for ${invitation.email}`}
              onClick={() => revoke.mutate(invitation.id)}
              disabled={revoke.isPending}
            >
              Withdraw
            </Button>
          </li>
        ))}
      </ul>
      {siteId && info.canManage ? <TransferInitiation siteId={siteId} /> : null}
    </>
  );
  if (!siteId) {
    return (
      <Collapsible.Root className="w-full">
        <CollapsibleTrigger>{title}</CollapsibleTrigger>
        <Collapsible.DefaultPanel>{body}</Collapsible.DefaultPanel>
      </Collapsible.Root>
    );
  }
  return (
    <section className="mx-auto my-6 p-4 border border-kumo-line max-w-216 rounded-[1rem] min-[601px]:p-6">
      <h2 className="text-[20px] font-semibold tracking-[-0.35px]">{title}</h2>
      {body}
    </section>
  );
}
